import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const AVAILABLE_SLOTS = [
  "Lundi 9h00 - 10h00",
  "Lundi 10h15 - 11h15",
  "Lundi 12h30 - 13h30",
  "Lundi 17h00 - 18h00",
  "Lundi 18h15 - 19h15",
  "Lundi 19h30 - 20h30",
  "Mardi 9h00 - 10h00",
  "Mardi 10h15 - 11h15",
  "Mardi 12h30 - 13h30",
  "Mardi 17h00 - 18h00",
  "Mardi 18h15 - 19h15",
  "Mardi 19h30 - 20h30",
  "Mercredi 9h00 - 10h00",
  "Mercredi 10h15 - 11h15",
  "Mercredi 12h30 - 13h30",
  "Mercredi 17h00 - 18h00",
  "Mercredi 18h15 - 19h15",
  "Mercredi 19h30 - 20h30",
  "Jeudi 9h00 - 10h00",
  "Jeudi 10h15 - 11h15",
  "Jeudi 12h30 - 13h30",
  "Jeudi 17h00 - 18h00",
  "Jeudi 18h15 - 19h15",
  "Jeudi 19h30 - 20h30",
  "Vendredi 9h00 - 10h00",
  "Vendredi 10h15 - 11h15",
  "Vendredi 12h30 - 13h30",
  "Vendredi 17h00 - 18h00",
  "Vendredi 18h15 - 19h15",
  "Vendredi 19h30 - 20h30",
  "Samedi 9h00 - 11h00",
];

export default function PersonalCoachingRequestForm({ open, onOpenChange }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    preferred_slot: "",
  });
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-\.]/g, '');
    if (!cleaned) return "Le numéro de téléphone est obligatoire";
    if (!/^\d{10}$/.test(cleaned)) {
      return "Le numéro doit contenir exactement 10 chiffres";
    }
    return "";
  };

  const handlePhoneChange = (value) => {
    setForm({ ...form, phone_number: value });
    const error = validatePhone(value);
    setPhoneError(error);
  };

  const submit = async (e) => {
    e.preventDefault();
    
    // Validate phone before submission
    const phoneValidationError = validatePhone(form.phone_number);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      toast.error(phoneValidationError);
      return;
    }
    
    if (!form.preferred_slot) {
      toast.error("Veuillez sélectionner un créneau");
      return;
    }
    
    if (!form.email) {
      toast.error("L'email est obligatoire pour le coaching individuel");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/personal-coaching/request", form);
      toast.success("Demande envoyée ! Samuel vous contactera pour confirmer votre séance.");
      onOpenChange(false);
      setForm({ first_name: "", last_name: "", email: "", phone_number: "", preferred_slot: "" });
      setPhoneError("");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">Coaching individuel · 1h</DialogTitle>
          <DialogDescription>
            Réservez votre séance de coaching personnalisé sur mesure.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={submit} className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pc_first_name">Prénom *</Label>
              <Input
                id="pc_first_name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="pc_last_name">Nom *</Label>
              <Input
                id="pc_last_name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="pc_email">Email *</Label>
            <Input
              id="pc_email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="pc_phone_number">Numéro de téléphone *</Label>
            <Input
              id="pc_phone_number"
              type="tel"
              value={form.phone_number}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="06 12 34 56 78"
              required
              className={`mt-1.5 ${phoneError ? 'border-red-500' : ''}`}
            />
            {phoneError && (
              <p className="text-xs text-red-600 mt-1">{phoneError}</p>
            )}
            <p className="text-xs text-stone-500 mt-1">10 chiffres requis</p>
          </div>

          <div>
            <Label htmlFor="pc_slot">Créneau souhaité *</Label>
            <Select value={form.preferred_slot} onValueChange={(val) => setForm({ ...form, preferred_slot: val })}>
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Sélectionnez un créneau" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {AVAILABLE_SLOTS.map((slot) => (
                  <SelectItem key={slot} value={slot}>
                    {slot}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-stone-100 p-4 text-sm text-stone-700">
            <p className="font-medium">Tarif : 50€ / séance</p>
            <p className="mt-1 text-xs">Paiement à régler sur place ou par virement avant la séance.</p>
          </div>

          <p className="text-xs text-stone-500 italic">
            *Sous réserve de disponibilité
          </p>

          <DialogFooter className="pt-4">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#8B7D6B] hover:bg-[#7A6C5B]">
              {loading ? "Envoi..." : "Envoyer ma demande"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
